import { Stack } from "@mui/material";
import { BoZoneScoreCard, IScoreCardOrientation } from "../../layer06";
import {
  CheckInSummary,
  CompletedCheckInSummary,
} from "app/containers/TeamsTab/types";
import {
  getLatestCheckIns,
  trendCategoryLabel,
} from "app/containers/TeamsTab/helpers";
import { BoZoneTrendDirection } from "../../layer01";
import { chartUtil } from "../../../common";
import { getS } from "utils/helperFunctions";

type BoZoneScoreListProps = {
  orientation?: IScoreCardOrientation;
  checkInSummary?: CompletedCheckInSummary | null;
  isLoading?: boolean;
};

interface IData {
  id: string;
  title: string;
  direction: BoZoneTrendDirection;
  value: string;
  summary: string;
  period: string;
  points: chartUtil.ChartPoints;
}

type CheckInMetric = "mood" | "progress" | "readiness";

const metricConfig: {
  metric: CheckInMetric;
  title: string;
  scoreKey: "moodScore" | "progressScore" | "readinessScore";
}[] = [
  { metric: "progress", title: "Progress", scoreKey: "progressScore" },
  { metric: "mood", title: "Mood", scoreKey: "moodScore" },
  { metric: "readiness", title: "Readiness", scoreKey: "readinessScore" },
];

const getScores = (summary?: CheckInSummary | null): IData[] => {
  if (!summary?.checkInCompleted) {
    return [];
  }

  const { scores, scoreTrends, trendDirection } =
    summary as CompletedCheckInSummary;
  const checkIns = getLatestCheckIns(summary as CompletedCheckInSummary);
  const period = checkIns.length
    ? `${checkIns.length} check-in${getS(checkIns.length)}`
    : "";

  return metricConfig.map(({ metric, title, scoreKey }) => {
    const score = scores?.[metric] ?? null;
    const category = trendDirection[metric];
    const trend = scoreTrends?.[metric];

    return {
      id: metric,
      title,
      direction: trend === "up" || trend === "down" ? trend : "flat",
      value: score != null ? score.toFixed(1) : "",
      summary: category ? trendCategoryLabel[category] : "No trend data",
      period,
      points: checkIns.map((checkIn) => checkIn[scoreKey] || null),
    };
  });
};

export const BoZoneScoreList = ({
  checkInSummary,
  orientation,
  isLoading,
}: BoZoneScoreListProps) => {
  const data = getScores(checkInSummary);

  return (
    <Stack
      direction={
        orientation ?? {
          xs: "column",
          sm: "row",
        }
      }
      columnGap=".75rem"
      rowGap=".75rem"
      alignItems="stretch"
    >
      {data.map((node) => (
        <BoZoneScoreCard
          key={node.id}
          orientation={orientation}
          title={node.title}
          direction={node.direction}
          value={node.value}
          summary={node.summary}
          period={node.period}
          points={node.points}
        />
      ))}
    </Stack>
  );
};
